import { useCallback, useMemo } from "react";
import { FaUser, FaWhatsapp } from "react-icons/fa";
import { FaRegComment } from "react-icons/fa6";

import { ContactPanelTabIds } from "@hd/contact-panel/types/contact-panel-tab-ids";
import { useTranslate } from "@hd/i18n/useTranslate";
import HdButton from "@hd/ui-kit/hd-button/HdButton";
import HdDropdownMenu from "@hd/ui-kit/hd-dropdown-menu/HdDropdownMenu";
import HdLoader from "@hd/ui-kit/hd-loader/HdLoader";
import HdTable from "@hd/ui-kit/hd-table/HdTable";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import HdCheckBox from "@hd/ui-kit/hd-check-box/HdCheckBox";
import ContactTableCell from "@hd/components/ContactTableCell";
import StayTableCell from "@hd/components/StayTableCell";
import { ScheduledCommunication } from "@hd/types/dto/communication/scheduled-communication.dto";
import { formatDateFromNow, formatTime } from "@hd/utils/dates";

interface ScheduledCommunicationsTableProps {
  data: ScheduledCommunication[];
  totalCount: number | null;
  onlineContactIds: number[];
  canLoadMore: boolean;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isConfirming: boolean;
  isCanceling: boolean;
  onLoadMore: () => void;
  onShowContactPanel: (
      contactId: number,
      tabId: ContactPanelTabIds
    ) => void;
  onShowPhoneNumberModal: (
    contactId: number,
    quotationId: string | null
  ) => void;
  onConfirm: (selectedIds: string[]) => void;
  onCancel: (selectedIds: string[]) => void;
}

const columnHelper = createColumnHelper<ScheduledCommunication>();

function ScheduledCommunicationsTable({
  data,
  isLoading,
  isFetchingNextPage,
  totalCount,
  onlineContactIds,
  canLoadMore,
  isConfirming,
  isCanceling,
  onShowContactPanel,
  onLoadMore,
  onShowPhoneNumberModal,
  onConfirm,
  onCancel,
}: ScheduledCommunicationsTableProps) {
  const isOnlineContact = useCallback(
    (contactId: number) => {
      return onlineContactIds.indexOf(contactId) >= 0;
    },
    [onlineContactIds]
  );

  const t = useTranslate();


  const columns = useMemo(() => {
    const cols = [
      columnHelper.display({
        id: "selection",
        cell: (props) => (
          <HdCheckBox
            checked={props.row.getIsSelected()}
            onChange={props.row.getToggleSelectedHandler()}
          />
        ),
        size: 50,
        meta: {
          flex: 0,
        },
      }),
      columnHelper.display({
        id: "contact",
        header: t("communicationPostStayPage.contact"),
        cell: (info) => (
          <ContactTableCell
            contactId={info.row.original.contactId}
            contactEmail={info.row.original.contactEmail}
            contactIsCompanyMember={info.row.original.contactIsCompanyMember}
            contactName={info.row.original.contactName}
            contactPhoneNumber={info.row.original.contactPhoneNumber}
            isOnline={isOnlineContact(info.row.original.contactId)}
            onClick={() =>
              info.row.original.contactEmail &&
              onShowContactPanel(
                info.row.original.contactId,
                ContactPanelTabIds.CONVERSATION
              )
            }
          ></ContactTableCell>
        ),
        size: 250,
        meta: {
          flex: 0,
        },
      }),
      columnHelper.display({
        id: "stay",
        header: t("communicationPostStayPage.stay"),
        cell: (info) => (
          <StayTableCell
            checkIn={info.row.original.checkIn ? new Date(info.row.original.checkIn) : undefined}
            checkOut={info.row.original.checkOut ? new Date(info.row.original.checkOut) : undefined}
            channelName={info.row.original.channelName}
            quotationCode={info.row.original.quotationCode}
          ></StayTableCell>
        ),
        meta: {
          flex: 1,
        },
      }),
      columnHelper.accessor("scheduledDateTime", {
        header: t("communicationPostStayPage.scheduledDate"),
        cell: (info) => (
          <div className="ml-0">
            <div className="pr-1">
              {formatDateFromNow(new Date(info.getValue()))}
            </div>
            <div>{formatTime(new Date(info.getValue()))}</div>
          </div>
        ),
        size: 200,
        meta: {
          flex: 0
        }
      }),
      columnHelper.display({
        header: t("common.options"),
        size: 250,
        cell: (info) => (
          <div className="flex items-center gap-2 justify-end">
            {!info.row.original.confirmed && <HdButton
              variant="primary"
              type="button"
              size="small"
              className="w-20"
              disabled={isConfirming}
              onClick={(e) => {
                e.stopPropagation();
                onConfirm(
                  [
                    info.row.original.id
                  ]
                  //Vecchia versione che passava anche communicationType
                  // [
                  //   {
                  //     id: info.row.original.id,
                  //     communicationType: info.row.original.communicationType
                  //   }
                  // ]
                );
              }}
            >
              {t("common.confirm")}
            </HdButton>}
            <HdButton
              variant="secondary"
              type="button"
              size="small"
              className="w-20"
              disabled={isCanceling}
              onClick={(e) => {
                e.stopPropagation();
                onCancel(
                  [
                    info.row.original.id
                  ]
                  //Vecchia versione che passava anche communicationType
                  // [
                  //   {
                  //     id: info.row.original.id,
                  //     communicationType: info.row.original.communicationType
                  //   }
                  // ]
                );
              }}
            >
              {t("common.cancel")}
            </HdButton>
            {info.row.original.contactId && (
              <HdDropdownMenu className="ml-2">
                <HdDropdownMenu.MenuItem
                  onAction={() =>
                    onShowContactPanel(
                    info.row.original.contactId,
                    ContactPanelTabIds.CONVERSATION
                    )
                  }
                  className={
                    "text-sm text-bodyText flex w-full items-center px-3 py-3 box-border outline-none cursor-pointer focus:bg-accent hover:bg-accent focus:text-white hover:text-white "
                  }
                >
                  <FaRegComment className="mr-2"></FaRegComment>
                  {t(
                    "common.viewConversation"
                  )}
                </HdDropdownMenu.MenuItem>
                  <HdDropdownMenu.MenuItem
                    className={
                      "text-sm text-bodyText flex w-full items-center px-3 py-3 box-border outline-none cursor-pointer focus:bg-accent hover:bg-accent focus:text-white hover:text-white "
                    }
                    onAction={() =>
                      onShowPhoneNumberModal(info.row.original.contactId, null)
                    }
                  >
                    <FaWhatsapp className="mr-2"></FaWhatsapp>
                    {t(
                      "common.contactOnWhatsapp"
                    )}
                  </HdDropdownMenu.MenuItem>
                  <HdDropdownMenu.MenuItem
                    onAction={() =>
                      onShowContactPanel(
                        info.row.original.contactId,
                        ContactPanelTabIds.CONTACT_INFO
                      )
                    }
                    className={
                      "text-sm text-bodyText flex w-full items-center px-3 py-3 box-border outline-none cursor-pointer focus:bg-accent hover:bg-accent focus:text-white hover:text-white "
                    }
                  >
                    <FaUser className="mr-2"></FaUser>
                    {t(
                      "common.viewPersonCard"
                    )}
                  </HdDropdownMenu.MenuItem>
              </HdDropdownMenu>
            )}
          </div>
        ),
      }),
    ];

    return cols;
  }, [
    t,
    isOnlineContact,
    onShowContactPanel,
    onShowPhoneNumberModal,
    onConfirm,
    onCancel,
    isConfirming,
    isCanceling
  ]);

  const table = useReactTable({
    data,
    rowCount: totalCount || undefined,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return isLoading ? (
    <HdLoader></HdLoader>
  ) : (
    <>
      <HdTable table={table}></HdTable>
      <div className="flex mt-4">
        <HdButton
          variant="primary"
          size="small"
          type="button"
          className="ml-2"
          disabled={isConfirming || table.getSelectedRowModel().flatRows.length === 0 || table.getSelectedRowModel().flatRows.some(r => r.original.confirmed)}
          onClick={(e) => {
            e.stopPropagation();
            //Vecchia versione che passava anche communicationType
            // const data = table.getSelectedRowModel().flatRows.map(r => ({id: r.original.id, communicationType: r.original.communicationType}));
            const data = table.getSelectedRowModel().flatRows.map(r => r.original.id);
            onConfirm(data);
            table.resetRowSelection();
          }}
        >{t("common.confirmSelected")}</HdButton>
        <HdButton
          variant="secondary"
          size="small"
          type="button"
          className="ml-2"
          disabled={isCanceling || table.getSelectedRowModel().flatRows.length === 0}
          onClick={(e) => {
            e.stopPropagation();
            //Vecchia versione che passava anche communicationType
            // const data = table.getSelectedRowModel().flatRows.map(r => ({id: r.original.id, communicationType: r.original.communicationType}));
            const data = table.getSelectedRowModel().flatRows.map(r => r.original.id);
            onCancel(data);
            table.resetRowSelection();
          }}
        >{t("common.cancelSelected")}</HdButton>
      </div>
      <HdTable.HdTableNextPageLoader
        table={table}
        canLoadMore={canLoadMore}
        isLoadingNextPage={isFetchingNextPage}
        onLoadMore={onLoadMore}
        countLabel={t("communicationPostStayPage.countLabel", {
          count: table.getRowCount(),
        })}
        loadMoreText={t(
          "communicationPostStayPage.showMoreCommunications"
        )}
      ></HdTable.HdTableNextPageLoader>
    </>
  );
}

export default ScheduledCommunicationsTable;
